import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Cookie, Settings } from 'lucide-react';
import { Button } from '@/components/ui/button';
import CookieConsentModal from './CookieConsentModal';

export default function CookieBanner() {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  console.log('[NEW CookieBanner] Component loaded, showBanner:', showBanner);

  useEffect(() => {
    console.log('[NEW CookieBanner] useEffect running');
    // Check if user has already made a choice
    const hasConsent = localStorage.getItem('ivoliq_cookie_consent');
    console.log('[NEW CookieBanner] hasConsent:', hasConsent);
    if (!hasConsent) {
      // Small delay for better UX
      console.log('[NEW CookieBanner] No consent found, will show banner in 1s');
      setTimeout(() => {
        console.log('[NEW CookieBanner] Setting showBanner to true');
        setShowBanner(true);
      }, 1000);
    } else {
      // Load existing preferences and initialize services
      console.log('[NEW CookieBanner] Consent found, loading preferences');
      loadExistingPreferences();
    }
  }, []);

  const loadExistingPreferences = () => {
    const analytics = localStorage.getItem('analytics-consent') === 'true';
    const marketing = localStorage.getItem('marketing-consent') === 'true';

    if (analytics) {
      initializePostHog();
    }

    if (marketing) {
      initializeGoogleTag();
    }
  };

  const initializePostHog = () => {
    if (window.posthog && typeof window.posthog.init === 'function') {
      window.posthog.init("phc_yJW1VjHGGwmCbbrtczfqqNxgBDbhlhOWcdzcIJEOTFE", {
        api_host: "https://eu.i.posthog.com",
        person_profiles: "identified_only",
      });
      console.log('[CookieBanner] PostHog initialized');
    }
  };

  const initializeGoogleTag = () => {
    // Check if gtag script is already loaded
    if (window.gtag) {
      console.log('[CookieBanner] Google Tag already loaded');
      return;
    }

    // Dynamically load gtag.js script
    const script = document.createElement('script');
    script.src = 'https://www.googletagmanager.com/gtag/js?id=AW-17764007604';
    script.async = true;
    script.onload = () => {
      // Initialize dataLayer and gtag
      window.dataLayer = window.dataLayer || [];
      function gtag() {
        window.dataLayer.push(arguments);
      }
      window.gtag = gtag;
      gtag('js', new Date());
      gtag('config', 'AW-17764007604');
      console.log('[CookieBanner] Google Tag initialized');
    };
    document.head.appendChild(script);
  };

  const handleSavePreferences = async (preferences) => {
    console.log('[CookieBanner] Saving preferences:', preferences);

    // Save to localStorage
    localStorage.setItem('analytics-consent', preferences.analytics.toString());
    localStorage.setItem('marketing-consent', preferences.marketing.toString());
    localStorage.setItem('ivoliq_cookie_consent', 'true');

    // Initialize services based on preferences
    if (preferences.analytics) {
      initializePostHog();
    } else {
      // Opt out of PostHog if loaded
      if (window.posthog && typeof window.posthog.opt_out_capturing === 'function') {
        window.posthog.opt_out_capturing();
      }
    }

    if (preferences.marketing) {
      initializeGoogleTag();
    }

    // Log consent to backend (GDPR compliance)
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${BACKEND_URL}/api/auth/cookie-consent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
        body: JSON.stringify({
          consent_type: preferences.analytics && preferences.marketing ? 'all' : preferences.analytics ? 'analytics_only' : preferences.marketing ? 'marketing_only' : 'necessary_only',
          analytics: preferences.analytics,
          marketing: preferences.marketing,
        }),
      });
      console.log('[CookieBanner] Consent logged to backend');
    } catch (error) {
      console.error('[CookieBanner] Failed to log consent:', error);
    }

    setShowBanner(false);
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
    };
    handleSavePreferences(allAccepted);
  };

  const handleNecessaryOnly = () => {
    const necessaryOnly = {
      necessary: true,
      analytics: false,
      marketing: false,
    };
    handleSavePreferences(necessaryOnly);
  };

  const handleCustomize = () => {
    setShowModal(true);
  };

  if (!showBanner) {
    return null;
  }

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-2xl border-t border-gray-700"
        style={{
          backdropFilter: 'blur(10px)',
          animation: 'slideUp 0.4s ease-out',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex flex-col lg:flex-row items-start lg:items-center gap-4">
            {/* Icon & Content */}
            <div className="flex items-start gap-4 flex-1">
              <Cookie className="h-8 w-8 text-blue-400 flex-shrink-0 mt-1" />
              <div className="flex-1">
                <h3 className="text-lg font-bold mb-2">
                  {t('cookies.title', 'Wir verwenden Cookies')}
                </h3>
                <p className="text-sm text-gray-300 mb-2">
                  {t(
                    'cookies.message',
                    'Wir nutzen notwendige Cookies für die Funktionalität, sowie optionale Cookies für Analysen und Marketing (Google Ads Tracking).'
                  )}
                </p>
                <Link to="/privacy" className="text-blue-400 hover:text-blue-300 underline text-sm font-medium">
                  {t('cookies.learnMore', 'Mehr erfahren')}
                </Link>
              </div>
            </div>

            {/* Buttons */}
            <div className="flex flex-col sm:flex-row gap-3 w-full lg:w-auto">
              <Button
                onClick={handleNecessaryOnly}
                variant="outline"
                className="bg-transparent border-gray-600 text-white hover:bg-gray-700 hover:text-white"
              >
                {t('cookies.decline', 'Nur notwendige')}
              </Button>
              <Button
                onClick={handleCustomize}
                variant="outline"
                className="bg-transparent border-gray-600 text-white hover:bg-gray-700 hover:text-white flex items-center gap-2"
              >
                <Settings className="h-4 w-4" />
                {t('cookies.customize', 'Anpassen')}
              </Button>
              <Button
                onClick={handleAcceptAll}
                className="bg-blue-600 hover:bg-blue-700 text-white font-semibold"
              >
                {t('cookies.accept', 'Alle akzeptieren')}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal */}
      <CookieConsentModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onSave={handleSavePreferences}
      />

      {/* Slide-up animation */}
      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(100%);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
      `}</style>
    </>
  );
}
